import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ClipboardList, Building2, ListChecks, Scale } from "lucide-react";

export interface NeilStep {
  letter: string;
  title: string;
  question: string;
  summary: string;
  points: string[];
  icon: typeof ClipboardList;
}

export const NEIL_STEPS: NeilStep[] = [
  {
    letter: "N",
    title: "Name the Need",
    question: "What operational problem are we solving?",
    summary:
      "Every engagement begins with the client's actual operational challenge — not a predetermined robot. Technology comes second. The operational need comes first.",
    points: [
      "Operational pain and bottlenecks",
      "Staffing challenges and repetitive work",
      "Service-level requirements",
      "Productivity constraints",
      "Business objectives",
    ],
    icon: ClipboardList,
  },
  {
    letter: "E",
    title: "Evaluate the Environment",
    question: "Where will automation operate?",
    summary:
      "A structured operational and site assessment establishes whether the environment can support automation — and what has to change before it can.",
    points: [
      "Facility size, floor plans and surface types",
      "Elevators, doors, hallways and loading areas",
      "Traffic patterns and operating hours",
      "Network environment, safety and access restrictions",
      "Outdoor terrain: acreage, slopes, boundaries, turf, charging locations",
    ],
    icon: Building2,
  },
  {
    letter: "I",
    title: "Itemize the Inputs",
    question: "What resources and processes exist today?",
    summary:
      "We document the current operation as it actually runs. We do not estimate critical customer information when verified data can be obtained.",
    points: [
      "Headcount, shifts and shift coverage",
      "Task frequency and labor hours",
      "Equipment and existing technology",
      "Workflow and service frequency requirements",
      "Productivity requirements and operational constraints",
    ],
    icon: ListChecks,
  },
  {
    letter: "L",
    title: "Lock the Labor Baseline",
    question: "What is the economic starting point?",
    summary:
      "Where labor economics are relevant, we confirm the fully loaded operating baseline. That baseline becomes the comparison anchor for automation economics. We never fabricate customer economics, and estimates are always labeled separately from customer-confirmed figures.",
    points: [
      "Hourly wages, payroll burden and benefits",
      "Overtime and staffing levels",
      "Contractor expenses",
      "Relevant equipment expenses",
      "Confirmed vs. estimated figures identified clearly",
    ],
    icon: Scale,
  },
];

export const LIFECYCLE = [
  { step: "DISCOVER", detail: "N.E.I.L.™ Method" },
  { step: "DESIGN", detail: "Automation Blueprint" },
  { step: "VALIDATE", detail: "ROI + Operational Analysis" },
  { step: "PILOT", detail: "Controlled Implementation" },
  { step: "DEPLOY", detail: "Installation + Mapping + Training" },
  { step: "MANAGE", detail: "RaaS + Support" },
  { step: "OPTIMIZE", detail: "Performance Analysis" },
  { step: "SCALE", detail: "Multi-Site Expansion" },
];

interface Props {
  /** Contextual label, e.g. "N.E.I.L.™ Facility Assessment" */
  contextLabel?: string;
  contextCopy?: string;
  showLifecycle?: boolean;
  compact?: boolean;
}

const NeilMethodSection = ({
  contextLabel,
  contextCopy,
  showLifecycle = false,
  compact = false,
}: Props) => {
  return (
    <section
      id="neil-method"
      aria-labelledby="neil-method-heading"
      className="scroll-mt-32 py-14 md:py-20"
    >
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto max-w-4xl text-center">
          <p className="mb-3 text-xs font-bold tracking-[0.32em] text-[#D6A84F]">
            {contextLabel ?? "THE N.E.I.L.™ METHOD"}
          </p>
          <h2
            id="neil-method-heading"
            className="font-orbitron text-3xl font-bold leading-tight text-foreground md:text-5xl"
          >
            We don’t start with technology.
            <br />
            We start with your operation.
          </h2>
          <p className="mx-auto mt-5 max-w-3xl text-base leading-relaxed text-muted-foreground md:text-lg">
            {contextCopy ??
              "Every Neil AI engagement begins with structured discovery. Before recommending technology, we work to understand the operational problem, environment, workflow requirements, existing resources, and economic baseline."}
          </p>
        </div>

        <div className={`mt-10 grid gap-5 ${compact ? "md:grid-cols-2 xl:grid-cols-4" : "md:grid-cols-2"}`}>
          {NEIL_STEPS.map((step) => {
            const Icon = step.icon;
            return (
              <article
                key={step.letter}
                className="group relative overflow-hidden rounded-[24px] border border-[hsl(var(--line))] bg-card/60 p-6 backdrop-blur-sm transition-all duration-300 hover:border-[#D6A84F]/40 hover:shadow-[0_0_40px_hsl(var(--primary)/0.18)]"
              >
                <div className="flex items-start gap-4">
                  <span className="font-orbitron flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-[#D6A84F]/40 bg-[#D6A84F]/10 text-2xl font-bold text-[#D6A84F]">
                    {step.letter}
                  </span>
                  <div className="min-w-0">
                    <h3 className="font-orbitron text-lg font-bold text-foreground md:text-xl">{step.title}</h3>
                    <p className="mt-1 text-sm text-primary">{step.question}</p>
                  </div>
                  <Icon className="ml-auto hidden h-5 w-5 shrink-0 text-muted-foreground sm:block" />
                </div>

                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{step.summary}</p>

                {!compact && (
                  <ul className="mt-4 space-y-2">
                    {step.points.map((point) => (
                      <li key={point} className="flex gap-2 text-sm text-foreground/85">
                        <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            );
          })}
        </div>

        <div className="mt-8 rounded-[24px] border border-[#D6A84F]/30 bg-[#D6A84F]/5 px-6 py-6 text-center">
          <p className="font-orbitron text-sm font-bold tracking-[0.2em] text-[#D6A84F]">
            DISCOVERY BEFORE PRESCRIPTION
          </p>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Neil AI does not recommend a final automation architecture until discovery is complete.
            N → E → I → L. Only then does solution engineering begin.
          </p>
        </div>

        {showLifecycle && (
          <div className="mt-10">
            <h3 className="font-orbitron text-center text-xl font-bold text-foreground md:text-2xl">
              The Neil AI Customer Lifecycle
            </h3>
            <ol className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {LIFECYCLE.map((item, index) => (
                <li
                  key={item.step}
                  className="rounded-[18px] border border-[hsl(var(--line))] bg-background/60 p-4"
                >
                  <span className="text-[11px] font-semibold tracking-[0.2em] text-muted-foreground">
                    STEP {index + 1}
                  </span>
                  <p className="font-orbitron mt-1 text-base font-bold text-foreground">{item.step}</p>
                  <p className="mt-1 text-sm text-primary">{item.detail}</p>
                </li>
              ))}
            </ol>
          </div>
        )}

        <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Button
            asChild
            size="lg"
            className="min-h-12 rounded-full bg-gradient-to-r from-[hsl(43_75%_55%)] to-[hsl(43_85%_65%)] px-8 font-semibold text-black hover:opacity-95"
          >
            <Link to="/neil-assessment">
              START YOUR N.E.I.L.™ ASSESSMENT
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="min-h-12 rounded-full border-primary/40 bg-background/50 px-8"
          >
            <Link to="/neil-method">EXPLORE THE METHOD</Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default NeilMethodSection;
